import {
  BitkitCheckbox,
  BitkitCheckboxGroup,
  BitkitDatePicker,
  BitkitNativeSelect,
  BitkitSwitch,
  BitkitTextInput,
  rem,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Separator } from '@chakra-ui/react/separator';
import { Text } from '@chakra-ui/react/text';
import { DateTime } from 'luxon';
import { useFormContext } from 'react-hook-form';

import TimeZoneDropdown from '@/pages/App/AppDetails/components/TimeZoneDropdown/TimeZoneDropdown';
import { dateInputDateFormat } from '@/utils/dateUtils';

import { BasicScheduleBuildType } from '../StartBuildDialog/types';
import { Day } from '../StartBuildDialog/utils/daysToNumeric';

const nthWeekdayOfMonth = (date: DateTime): number => {
  return Math.floor((date.day - 1) / 7) + 1;
};

export const getWeekOfMonthStr = (date: DateTime) => {
  const numberToString: Record<number, string> = {
    1: 'first',
    2: 'second',
    3: 'third',
    4: 'fourth',
    5: 'fifth',
  };
  return numberToString[nthWeekdayOfMonth(date)];
};

const BasicBuildSchedule = () => {
  const { register, watch, setValue } = useFormContext<BasicScheduleBuildType>();

  const isRepetitionToggled = watch('repetitionEnabled');
  const repeatEvery = watch('repeatEvery');
  const repeatInterval = watch('repeatInterval');
  const startDate = watch('startDate');

  const dateFormatter = DateTime.fromFormat(startDate || '', dateInputDateFormat);

  const suffix = repeatEvery && repeatEvery > 1 ? 's' : '';

  return (
    <div>
      <Box display="flex" gap="16" marginBlockEnd="24">
        <BitkitDatePicker
          value={dateFormatter.isValid ? dateFormatter.toJSDate() : null}
          onValueChange={(date) => {
            if (date) {
              setValue('startDate', DateTime.fromJSDate(date).toFormat(dateInputDateFormat));
            }
          }}
          label="Start date and time"
          flex="1"
          data-testid="start-date"
        />
        <BitkitTextInput
          data-testid="time"
          flex="1"
          label="Time"
          inputProps={{ ...register('time'), type: 'time', ...{ 'data-testid': 'time' } }}
        />
      </Box>
      <TimeZoneDropdown
        data-testid="timezone"
        locale={watch('setupLocation') || ''}
        onTimeZoneChange={(timeZone) => {
          setValue('setupLocation', timeZone);
        }}
      />
      <Separator marginBlock="24" />
      <Text textStyle="body/md/semibold" marginBlockEnd="4">
        Repetition
      </Text>
      <BitkitSwitch
        checked={isRepetitionToggled}
        data-testid="repetition-enabled"
        onCheckedChange={({ checked }) => {
          setValue('repetitionEnabled', checked);
        }}
        optional
        valueText={isRepetitionToggled ? 'Enabled' : 'Disabled'}
        variant="dynamic-text"
      />
      {isRepetitionToggled && (
        <>
          <Box display="flex" alignItems="flex-end" gap="16" marginBlockStart="24">
            <BitkitTextInput
              data-testid="repeat-every"
              label="Repeat every"
              width={rem(136)}
              inputProps={{
                ...register('repeatEvery'),
                max: 100,
                min: 1,
                type: 'number',
                ...{ 'data-testid': 'repeat-every' },
              }}
            />
            <BitkitNativeSelect {...register('repeatInterval')} optional width="136px" data-testid="repeat-interval">
              <option key="minute" value="minute">
                minute{suffix}
              </option>
              <option key="hour" value="hour">
                hour{suffix}
              </option>
              <option key="day" value="day">
                day{suffix}
              </option>
              <option key="week" value="week">
                week{suffix}
              </option>
              <option key="month" value="month">
                month{suffix}
              </option>
              <option key="year" value="year">
                year{suffix}
              </option>
            </BitkitNativeSelect>
            {repeatInterval === 'month' && (
              <BitkitNativeSelect {...register('monthlyRepetition')} optional width="288px" data-testid="day-select">
                <option key="day" value="day">
                  On day {dateFormatter.day}
                </option>
                <option key="weekday" value="weekday">
                  On the {getWeekOfMonthStr(dateFormatter)} {dateFormatter.weekdayLong}
                </option>
              </BitkitNativeSelect>
            )}
            {repeatInterval === 'year' && (
              <BitkitNativeSelect {...register('yearlyRepetition')} optional width="288px" data-testid="day-select">
                <option key="day" value="day">
                  On {dateFormatter.monthShort} {dateFormatter.day}
                </option>
                <option key="weekday" value="weekday">
                  On the {getWeekOfMonthStr(dateFormatter)} {dateFormatter.weekdayLong} of {dateFormatter.monthLong}
                </option>
              </BitkitNativeSelect>
            )}
          </Box>
          {repeatInterval === 'week' && (
            <Box marginBlockStart="24">
              <BitkitCheckboxGroup
                groupLabel="Repeat on"
                onValueChange={(days) => setValue('repeatOn', days as Day[])}
                value={watch('repeatOn') ?? []}
              >
                <Box display="flex" flexWrap="wrap" gap="16">
                  <BitkitCheckbox labelText="Mon" value="MO" />
                  <BitkitCheckbox labelText="Tue" value="TU" />
                  <BitkitCheckbox labelText="Wed" value="WE" />
                  <BitkitCheckbox labelText="Thu" value="TH" />
                  <BitkitCheckbox labelText="Fri" value="FR" />
                  <BitkitCheckbox labelText="Sat" value="SA" />
                  <BitkitCheckbox labelText="Sun" value="SU" />
                </Box>
              </BitkitCheckboxGroup>
            </Box>
          )}
        </>
      )}
    </div>
  );
};

export default BasicBuildSchedule;
